import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useEffect, useRef, useState } from "react";
import htmlDocx from "html-docx-js/dist/html-docx";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { singleUploadToS3 } from "../../../redux/actions/action";
import RenderTemplateElement from "./RenderTemplateElement";
import { normalizeElementStyles } from "./render-model";

// import domtoimage from 'dom-to-image';
const BUILDER_CANVAS_WIDTH = 800;
const BUILDER_CANVAS_HEIGHT = 1200;
const HEADER_ZONE_HEIGHT = 120;
const FOOTER_ZONE_HEIGHT = 120;
const PAGE_BODY_PADDING = 24;
const PRINT_ROW_COLLISION_GAP = 4;
const STRICT_PRINT_COORDINATES = true;
const PAGE_FIXED_PLACEHOLDERS = new Set([
  "{HEADER_TEXT}",
  "{FOOTER_TEXT}",
  "{BACKGROUND_IMAGE}",
  "{COMPANY_LOGO}",
  "{SECONDARY_LOGO}",
  "{WATERMARK}",
]);

const normalizePlaceholder = (value) =>
  String(value || "")
    .trim()
    .toUpperCase();

const isAllPageFixedPlaceholder = (placeholder) =>
  placeholder === "{BACKGROUND_IMAGE}" || placeholder === "{WATERMARK}";

const getElementTop = (ref) =>
  Number(
    String(ref?.el?.style?.top ?? ref?.item?.y ?? 0).replace("px", ""),
  ) || 0;

const getElementLeft = (ref) =>
  Number(
    String(ref?.el?.style?.left ?? ref?.item?.x ?? 0).replace("px", ""),
  ) || 0;

const getElementBottom = (ref) => getElementTop(ref) + getNodeHeight(ref);

const buildPrintableTableClone = (elementRef, rowIndexes = null) => {
  const clone = cloneNodeForPage(elementRef, getElementTop(elementRef), getElementLeft(elementRef));
  if (!clone) {
    return null;
  }
  const rowIndexSet =
    Array.isArray(rowIndexes) && rowIndexes.length > 0
      ? new Set(rowIndexes)
      : null;
  if (!rowIndexSet) {
    return clone;
  }

  const tbodyRows = Array.from(clone.querySelectorAll("tbody tr"));
  if (tbodyRows.length === 0) {
    return clone;
  }

  tbodyRows.forEach((row, index) => {
    if (!rowIndexSet.has(index)) {
      row.remove();
    }
  });

  return clone;
};

const isHeaderZoneRef = (ref) => {
  const placeholder = normalizePlaceholder(ref?.item?.fieldPlaceHolder);
  return (
    !isAllPageFixedPlaceholder(placeholder) &&
    getElementTop(ref) < HEADER_ZONE_HEIGHT
  );
};

const isFooterZoneRef = (ref) => {
  const placeholder = normalizePlaceholder(ref?.item?.fieldPlaceHolder);
  return (
    !isAllPageFixedPlaceholder(placeholder) &&
    getElementTop(ref) >= BUILDER_CANVAS_HEIGHT - FOOTER_ZONE_HEIGHT
  );
};

const getHeaderBodyTop = (validRefs) => {
  const headerBottom = validRefs
    .filter(isHeaderZoneRef)
    .reduce((max, ref) => Math.max(max, getElementBottom(ref)), 0);
  return Math.max(HEADER_ZONE_HEIGHT, headerBottom + PAGE_BODY_PADDING);
};

const getFooterBodyBottom = (validRefs) => {
  const footerTop = validRefs
    .filter(isFooterZoneRef)
    .reduce(
      (min, ref) => Math.min(min, getElementTop(ref)),
      BUILDER_CANVAS_HEIGHT,
    );
  return Math.max(
    HEADER_ZONE_HEIGHT + PAGE_BODY_PADDING,
    Math.min(
      BUILDER_CANVAS_HEIGHT - FOOTER_ZONE_HEIGHT,
      footerTop - PAGE_BODY_PADDING,
    ),
  );
};

const getTableMetrics = (elementRef) => {
  const tableClone = buildPrintableTableClone(elementRef);
  const tableNode = tableClone?.querySelector("table");
  if (!tableNode) {
    return {
      headerHeight: 0,
      rowHeights: [],
      totalHeight: elementRef?.el?.offsetHeight || 0,
    };
  }

  const measurementHost = document.createElement("div");
  measurementHost.style.position = "absolute";
  measurementHost.style.left = "-20000px";
  measurementHost.style.top = "0px";
  measurementHost.style.width = `${BUILDER_CANVAS_WIDTH}px`;
  measurementHost.style.visibility = "hidden";
  measurementHost.appendChild(tableClone);
  document.body.appendChild(measurementHost);

  const headerRow = tableNode.querySelector("thead tr");
  const headerHeight = headerRow?.offsetHeight || 0;
  const rowNodes = Array.from(tableNode.querySelectorAll("tbody tr"));
  const rowHeights = rowNodes.map((row) => row.offsetHeight || 0);
  const totalHeight =
    headerHeight + rowHeights.reduce((sum, height) => sum + height, 0);

  measurementHost.remove();

  return {
    headerHeight,
    rowHeights,
    totalHeight,
  };
};

const getNodeHeight = (ref) => ref?.el?.offsetHeight || 0;

const getCloneNodeMetrics = (node) => ({
  top: toPxNumber(node?.style?.top, 0),
  left: toPxNumber(node?.style?.left, 0),
  height:
    node?.offsetHeight ||
    node?.getBoundingClientRect?.().height ||
    toPxNumber(node?.style?.height, 0) ||
    0,
});

const reflowPdfClone = (pageClone, pageHeight, sourceItems = []) => {
  const sourceItemMap = new Map(
    sourceItems.map((item) => [String(item?.id), item]),
  );
  const nodes = Array.from(pageClone.querySelectorAll('[id^="print-element-"]'));
  const bodyNodes = nodes.filter((node) => {
    const nodeId = String(node?.id || "").replace("print-element-", "");
    const sourceItem = sourceItemMap.get(nodeId);
    const placeholder = normalizePlaceholder(sourceItem?.fieldPlaceHolder);
    return !PAGE_FIXED_PLACEHOLDERS.has(placeholder);
  });

  const rowsByTop = new Map();
  bodyNodes.forEach((node) => {
    const metrics = getCloneNodeMetrics(node);
    const rowKey = String(metrics.top);
    if (!rowsByTop.has(rowKey)) {
      rowsByTop.set(rowKey, {
        originalTop: metrics.top,
        nodes: [],
        rowHeight: 0,
      });
    }

    const row = rowsByTop.get(rowKey);
    row.nodes.push(node);
    row.rowHeight = Math.max(row.rowHeight, metrics.height);
  });

  const rows = Array.from(rowsByTop.values()).sort(
    (a, b) => a.originalTop - b.originalTop,
  );

  let previousOriginalBottom = rows.length > 0 ? rows[0].originalTop : 0;
  let previousReflowBottom = rows.length > 0 ? rows[0].originalTop : 0;

  rows.forEach((row, index) => {
    if (index === 0) {
      previousOriginalBottom = row.originalTop + row.rowHeight;
      previousReflowBottom = row.originalTop + row.rowHeight;
    } else {
      const originalGap = Math.max(
        0,
        row.originalTop - previousOriginalBottom,
      );
      const candidateTop = previousReflowBottom + originalGap;
      const reflowTop = Math.max(row.originalTop, candidateTop);

      row.nodes.forEach((node) => {
        node.style.top = `${reflowTop}px`;
      });

      previousOriginalBottom = row.originalTop + row.rowHeight;
      previousReflowBottom = reflowTop + row.rowHeight;
    }
  });

  const maxBottom = rows.reduce((max, row) => {
    const rowTop = getCloneNodeMetrics(row.nodes[0]).top;
    return Math.max(max, rowTop + row.rowHeight);
  }, pageHeight);

  pageClone.style.height = `${Math.max(pageHeight, maxBottom + PAGE_BODY_PADDING)}px`;
  return pageClone;
};

const cloneNodeForPage = (elementRef, top, left) => {
  const clone = elementRef?.el?.cloneNode(true);
  if (!clone) {
    return null;
  }
  clone.style.position = "absolute";
  clone.style.top = `${Math.max(0, top)}px`;
  clone.style.left = `${Math.max(0, left)}px`;
  clone.style.zIndex = elementRef?.el?.style?.zIndex || "10";
  return clone;
};

const cloneTableSegmentForPage = (elementRef, top, left, rowIndexes, width) => {
  const clone = buildPrintableTableClone(elementRef, rowIndexes) || cloneNodeForPage(elementRef, top, left);
  if (!clone) {
    return null;
  }
  clone.style.position = "absolute";
  clone.style.top = `${Math.max(0, top)}px`;
  clone.style.left = `${Math.max(0, left)}px`;
  if (typeof width === "number" && Number.isFinite(width) && width > 0) {
    clone.style.width = `${width}px`;
    clone.style.maxWidth = `${width}px`;
  }
  clone.style.boxSizing = "border-box";
  clone.style.zIndex = elementRef?.el?.style?.zIndex || "10";
  return clone;
};

const buildPageModels = (validRefs) => {
  const orderedRefs = [...validRefs].sort(
    (a, b) => getElementTop(a) - getElementTop(b) || getElementLeft(a) - getElementLeft(b),
  );
  const isFixedPlaceholder = (placeholder) =>
    PAGE_FIXED_PLACEHOLDERS.has(placeholder) || isAllPageFixedPlaceholder(placeholder);

  const tableRef = orderedRefs.find(
    (ref) => normalizePlaceholder(ref?.item?.fieldPlaceHolder) === "{TABLE}",
  );
  const tableMetrics = tableRef ? getTableMetrics(tableRef) : null;
  const tableTop = tableRef ? getElementTop(tableRef) : 0;
  const tableLeft = tableRef ? getElementLeft(tableRef) : 0;
  const tableWidth = tableRef
    ? Math.max(120, BUILDER_CANVAS_WIDTH - tableLeft * 2)
    : 0;
  const bodyRefs = orderedRefs.filter((ref) => {
    const placeholder = normalizePlaceholder(ref?.item?.fieldPlaceHolder);
    return (
      placeholder !== "{TABLE}" &&
      !isFixedPlaceholder(placeholder)
    );
  });
  const preTableBodyRefs = tableRef
    ? bodyRefs.filter((ref) => getElementTop(ref) < tableTop)
    : bodyRefs;
  const postTableBodyRefs = tableRef
    ? bodyRefs.filter((ref) => getElementTop(ref) >= tableTop)
    : [];

  const rowHeights = tableMetrics?.rowHeights || [];
  const tableHeaderHeight = tableMetrics?.headerHeight || 0;
  const firstPageAvailable = tableRef
    ? Math.max(0, BUILDER_CANVAS_HEIGHT - FOOTER_ZONE_HEIGHT - tableTop)
    : 0;
  const middlePageAvailable = Math.max(
    0,
    BUILDER_CANVAS_HEIGHT - FOOTER_ZONE_HEIGHT - PAGE_BODY_PADDING,
  );
  const lastBodyTop = postTableBodyRefs.length
    ? Math.min(...postTableBodyRefs.map((ref) => getElementTop(ref)))
    : BUILDER_CANVAS_HEIGHT - FOOTER_ZONE_HEIGHT;
  const lastPageAvailable = Math.max(
    0,
    Math.min(BUILDER_CANVAS_HEIGHT - FOOTER_ZONE_HEIGHT, lastBodyTop) -
      PAGE_BODY_PADDING,
  );

  const totalRowsHeight = rowHeights.reduce((sum, height) => sum + height, 0);
  const segments = [];

  if (tableRef) {
    let rowIndex = 0;
    let remainingHeight = totalRowsHeight;
    let pageIndex = 0;

    while (rowIndex < rowHeights.length || (rowHeights.length === 0 && pageIndex === 0)) {
      const capacity =
        pageIndex === 0
          ? firstPageAvailable
          : tableHeaderHeight + remainingHeight <= lastPageAvailable
            ? lastPageAvailable
            : middlePageAvailable;
      const segmentRows = [];
      let usedHeight = tableHeaderHeight;

      if (rowHeights.length === 0) {
        segments.push({
          ref: tableRef,
          top: pageIndex === 0 ? tableTop : PAGE_BODY_PADDING,
          left: tableLeft,
          width: tableWidth,
          height: tableHeaderHeight,
          rowIndexes: [],
        });
        break;
      }

      while (rowIndex < rowHeights.length) {
        const rowHeight = rowHeights[rowIndex] || 0;
        const wouldOverflow = usedHeight + rowHeight > capacity;
        if (wouldOverflow && segmentRows.length > 0) {
          break;
        }
        segmentRows.push(rowIndex);
        usedHeight += rowHeight;
        remainingHeight -= rowHeight;
        rowIndex += 1;
        if (wouldOverflow) {
          break;
        }
      }

      segments.push({
        ref: tableRef,
        top: pageIndex === 0 ? tableTop : PAGE_BODY_PADDING,
        left: tableLeft,
        width: tableWidth,
        height: tableHeaderHeight + segmentRows.reduce(
          (sum, rowIndexValue) => sum + (rowHeights[rowIndexValue] || 0),
          0,
        ),
        rowIndexes: segmentRows,
      });
      pageIndex += 1;

      if (segmentRows.length === 0) {
        break;
      }
    }
  }

  const pageCount = Math.max(
    1,
    segments.length || (bodyRefs.length > 0 ? 1 : 0),
  );
  const pageModels = Array.from({ length: pageCount }, (_, index) => ({
    bodyNodes: [],
    tableSegment: segments[index] || null,
  }));

  if (tableRef) {
    const bodyPlacementPage = pageCount === 1 ? 0 : pageCount - 1;
    const bodyPlacementTop = (() => {
      const tableSegment = pageModels[bodyPlacementPage]?.tableSegment;
      const firstPostTop = postTableBodyRefs.length
        ? Math.min(...postTableBodyRefs.map((ref) => getElementTop(ref)))
        : null;
      if (!tableSegment || firstPostTop === null) {
        return null;
      }
      const tableBottom = (tableSegment.top || 0) + (tableSegment.height || 0);
      return Math.max(firstPostTop, tableBottom + PAGE_BODY_PADDING);
    })();
    preTableBodyRefs.forEach((ref) => {
      pageModels[0].bodyNodes.push({
        ref,
        top: getElementTop(ref),
        left: getElementLeft(ref),
      });
    });
    postTableBodyRefs.forEach((ref) => {
      pageModels[bodyPlacementPage].bodyNodes.push({
        ref,
        top:
          bodyPlacementTop === null
            ? getElementTop(ref)
            : bodyPlacementTop + (getElementTop(ref) - Math.min(...postTableBodyRefs.map((postRef) => getElementTop(postRef)))),
        left: getElementLeft(ref),
      });
    });
  } else {
    bodyRefs.forEach((ref) => {
      pageModels[0].bodyNodes.push({
        ref,
        top: getElementTop(ref),
        left: getElementLeft(ref),
      });
    });
  }

  return pageModels;
};

const waitForImages = async (rootNode) => {
  const imageNodes = Array.from(rootNode?.querySelectorAll("img") || []);
  if (imageNodes.length === 0) {
    return;
  }

  await Promise.all(
    imageNodes.map(
      (imageNode) =>
        new Promise((resolve) => {
          if (imageNode.complete && imageNode.naturalWidth > 0) {
            resolve();
            return;
          }
          const finalize = () => resolve();
          imageNode.addEventListener("load", finalize, { once: true });
          imageNode.addEventListener("error", finalize, { once: true });
        }),
    ),
  );
};

// hex to rgba helper function
function hexToRgb(hex) {
  // Remove the # if present
  hex = hex.replace(/^#/, "");

  if (hex.length === 3) {
    // Handle short format like #f00
    hex = hex
      .split("")
      .map((h) => h + h)
      .join("");
  }

  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return [r, g, b];
}

const toPxNumber = (value, fallback = 0) => {
  const parsed = parseFloat(String(value || "").replace("px", ""));
  return Number.isNaN(parsed) ? fallback : parsed;
};

const getStyledImageDimensions = (
  styles = {},
  imgProps = {},
  defaultWidth = 100,
) => {
  const sourceWidth = Number(imgProps.width) || defaultWidth;
  const sourceHeight = Number(imgProps.height) || defaultWidth;
  const ratio = sourceHeight / sourceWidth || 1;

  const widthFromStyle = toPxNumber(styles.width, 0);
  const heightFromStyle = toPxNumber(styles.height, 0);

  if (widthFromStyle > 0 && heightFromStyle > 0) {
    return { width: widthFromStyle, height: heightFromStyle };
  }
  if (widthFromStyle > 0) {
    return {
      width: widthFromStyle,
      height: Math.max(1, widthFromStyle * ratio),
    };
  }
  if (heightFromStyle > 0) {
    return {
      width: Math.max(1, heightFromStyle / ratio),
      height: heightFromStyle,
    };
  }

  return {
    width: defaultWidth,
    height: Math.max(1, defaultWidth * ratio),
  };
};

const drawZoneBorderSide = (
  pdf,
  side,
  width,
  color,
  style,
  pageWidth,
  zoneTop,
  zoneBottom,
) => {
  if (!width || width <= 0 || style === "none") {
    return;
  }
  const [r, g, b] = hexToRgb(color || "#000000");
  pdf.setDrawColor(r, g, b);
  pdf.setLineWidth(width);
  if (style === "dashed") {
    pdf.setLineDashPattern([4, 2], 0);
  } else if (style === "dotted") {
    pdf.setLineDashPattern([1, 2], 0);
  } else if (style === "double") {
    pdf.setLineDashPattern([6, 2], 0);
  } else {
    pdf.setLineDashPattern([], 0);
  }

  if (side === "top") {
    pdf.line(0, zoneTop, pageWidth, zoneTop);
  }
  if (side === "bottom") {
    pdf.line(0, zoneBottom, pageWidth, zoneBottom);
  }
  if (side === "left") {
    pdf.line(0, zoneTop, 0, zoneBottom);
  }
  if (side === "right") {
    pdf.line(pageWidth, zoneTop, pageWidth, zoneBottom);
  }
  pdf.setLineDashPattern([], 0);
};

const waitForNextPaint = () =>
  new Promise((resolve) => {
    if (
      typeof window !== "undefined" &&
      typeof window.requestAnimationFrame === "function"
    ) {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(resolve);
      });
      return;
    }

    setTimeout(resolve, 32);
  });

const waitForPdfAssetsToSettle = async (root) => {
  if (!root) return;

  try {
    if (typeof document !== "undefined" && document.fonts?.ready) {
      await document.fonts.ready;
    }
  } catch (error) {
    console.log("font readiness wait skipped", error);
  }

  const images = Array.from(root.querySelectorAll("img"));
  await Promise.all(
    images.map((img) => {
      if (img.complete) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const finish = () => {
          img.removeEventListener("load", finish);
          img.removeEventListener("error", finish);
          resolve();
        };

        img.addEventListener("load", finish, { once: true });
        img.addEventListener("error", finish, { once: true });
      });
    }),
  );

  await waitForNextPaint();
};

const BuilderOutput = ({
  children,
  items,
  onRequestData,
  onDownloadComplete,
  disabled,
  genrateLink,
  setGenrateLink,
  childDataLink,
  blockPDFDownload,
}) => {
  const dispatch = useDispatch();
  const [bulderElements, setbulderElements] = useState([]);
  const [isRequested, setIsRequested] = useState(false);
  const elementsRef = useRef([]);
  const uploadLockRef = useRef(false);
  const pdfUploadRetryRef = useRef(0);
  console.log("items ==========>", items);

  useEffect(() => {
    if (items && isRequested) {
      const resolvedItems = Array.isArray(items)
        ? items
        : Array.isArray(items.data)
          ? items.data
          : [];

      if (resolvedItems.length === 0) {
        return;
      }
      setbulderElements([...resolvedItems].sort((a, b) => a.y - b.y));
      if (items.message === "Invoice word data") {
        setTimeout(() => {
          exportToDocx(); // Call the function for Word
        }, 1000);
      } else if (
        items.message === "Invoice pdf data" ||
        items.message === "Purchasing order pdf data" ||
        items.message === "Quote pdf data"
      ) {
        setTimeout(() => {
          exportToPDF2(); // Call the function for PDF
        }, 1000);
      } else {
        setTimeout(() => {
          exportToPDF2();
        }, 1000);
      }
    }
  }, [items]);

  // Function to strip HTML while keeping line breaks from <p> and <br> tags
  function stripHtmlWithLineBreaks(html) {
    const withLineBreaks = html.replace(/<\/?[^>]+(>|$)/g, "\n");
    const div = document.createElement("div");
    div.innerHTML = withLineBreaks;
    return (div.textContent || div.innerText || "")
      .replace(/\n\s*\n/g, "\n")
      .trim();
  }
  const exportToPDF = () => {
    const usedElements = items?.data ? items.data : items;
    // Phase 1: apply raw canvas coordinates.
    const validRefs = elementsRef.current.filter(
      (elementRef) => elementRef?.el && elementRef?.item,
    );

    const positionedContentRefs = validRefs.filter((elementRef) => {
      const placeholder = String(
        elementRef.item.fieldPlaceHolder || "",
      ).toUpperCase();
      return (
        placeholder !== "{TABLE}" &&
        placeholder !== "{LINE}" &&
        placeholder !== "{WATERMARK}" &&
        placeholder !== "{BACKGROUND_IMAGE}" &&
        placeholder !== "{Header_Text}" &&
        placeholder !== "{Footer_Text}" &&
        placeholder !== "{Secondary_Logo}"
      );
    });

    const minContentLeft =
      positionedContentRefs.length > 0
        ? Math.min(...positionedContentRefs.map((x) => Number(x.item.x) || 0))
        : 0;
    const tableFrameLeft = Math.max(0, minContentLeft);
    const tableFrameWidth = Math.max(
      120,
      BUILDER_CANVAS_WIDTH - tableFrameLeft - PAGE_BODY_PADDING,
    );

    validRefs.forEach((elementRef) => {
      const isTableElement = elementRef.item.fieldPlaceHolder === "{TABLE}";
      const elementStyles = normalizeElementStyles(elementRef.item);
      const isTextWrapEnabled =
        elementStyles.textWrap === true || elementStyles.textWrap === "true";
      const itemX = Number(elementRef.item.x) || 0;
      const itemY = Number(elementRef.item.y) || 0;
      const availableWidth = Math.max(120, BUILDER_CANVAS_WIDTH - itemX);
      const rightNeighbor = validRefs
        .filter((x) => x.item.id !== elementRef.item.id)
        .filter((x) => (Number(x.item.y) || 0) === itemY)
        .filter((x) => (Number(x.item.x) || 0) > itemX)
        .sort((a, b) => (Number(a.item.x) || 0) - (Number(b.item.x) || 0))[0];
      const wrapWidthFromNeighbor = rightNeighbor
        ? Math.max(80, (Number(rightNeighbor.item.x) || 0) - itemX - 8)
        : availableWidth;
      const constrainedWrapWidth = `${Math.min(availableWidth, wrapWidthFromNeighbor)}px`;
      elementRef.el.style.top = `${elementRef.item.y}px`;
      elementRef.el.style.left = isTableElement
        ? `${tableFrameLeft}px`
        : `${elementRef.item.x}px`;
      if (isTableElement) {
        elementRef.el.style.width = `${tableFrameWidth}px`;
      } else if (isTextWrapEnabled) {
        elementRef.el.style.width = elementStyles.width || constrainedWrapWidth;
        elementRef.el.style.maxWidth =
          elementStyles.maxWidth || constrainedWrapWidth;
      } else {
        elementRef.el.style.width = "auto";
        elementRef.el.style.maxWidth = "none";
      }
    });

    if (!STRICT_PRINT_COORDINATES) {
      // Phase 2: optional overlap correction.
      const rowsByY = validRefs.reduce((acc, elementRef) => {
        const yKey = Number(elementRef.item.y) || 0;
        if (!acc[yKey]) {
          acc[yKey] = [];
        }
        acc[yKey].push(elementRef);
        return acc;
      }, {});

      const sortedRows = Object.keys(rowsByY)
        .map((key) => Number(key))
        .sort((a, b) => a - b)
        .map((rowY) => {
          const rowElements = rowsByY[rowY] || [];
          const rowHeight = rowElements.reduce(
            (maxHeight, elementRef) =>
              Math.max(maxHeight, elementRef.el.offsetHeight || 0),
            0,
          );
          return {
            originalTop: rowY,
            originalBottom: rowY + rowHeight,
            adjustedTop: rowY,
            adjustedBottom: rowY + rowHeight,
            rowHeight,
            elements: rowElements,
          };
        });

      sortedRows.forEach((row, index) => {
        if (index === 0) {
          row.adjustedTop = row.originalTop;
        } else {
          const prevRow = sortedRows[index - 1];
          row.adjustedTop = Math.max(
            row.originalTop,
            prevRow.adjustedBottom + PRINT_ROW_COLLISION_GAP,
          );
        }

        row.adjustedBottom = row.adjustedTop + row.rowHeight;
        row.elements.forEach((elementRef) => {
          elementRef.el.style.top = `${row.adjustedTop}px`;
        });
      });
    }

    var content = document.getElementById("builder-output-1");
    const margin = { top: 20, right: 0, bottom: 20, left: 0 };
    const pageWidth = BUILDER_CANVAS_WIDTH; // Keep aligned with canvas width
    const pageHeight = BUILDER_CANVAS_HEIGHT;
    let headerHeight = HEADER_ZONE_HEIGHT; // Header zone height
    const footerHeight = FOOTER_ZONE_HEIGHT; // Footer zone height
    var contentHeight = content.scrollHeight;
    let position = 0;
    const pages = Math.ceil(contentHeight / pageHeight);
    const pdf = new jsPDF("p", "px", [pageWidth, pageHeight]);
    const canvasPromises = [];

    // Loop through each page, capturing each section of the content
    for (let i = 0; i < pages; i++) {
      (function (position) {
        canvasPromises.push(
          html2canvas(content, {
            windowWidth: content.scrollWidth,
            height: pageHeight,
            y: position,
            x: 0,
            scale: 3.5,
            removeContainer: true,
            backgroundColor: "transparent",
          }),
        );
      })(position);
      position += pageHeight;
    }

    // After all canvases are rendered, add them to the PDF
    Promise.all(canvasPromises).then(function (canvases) {
      canvases.forEach((canvas, index) => {
        const imgData = canvas.toDataURL("image/png");
        if (index > 0) {
          pdf.addPage();
        }

        // Draw canvas content at true page origin so header/footer-zone elements
        // keep the same Y placement as the builder canvas.
        try {
          pdf.addImage(imgData, "PNG", 0, 0, pageWidth, pageHeight);
        } catch (error) {
          console.log("error in pdf generation", error);
        }

        const headerZoneElement = usedElements.find(
          (x) => x.fieldPlaceHolder === "{Header_Text}",
        );
        const footerZoneElement = usedElements.find(
          (x) => x.fieldPlaceHolder === "{Footer_Text}",
        );

        if (headerZoneElement && index === 0) {
          const hs = normalizeElementStyles(headerZoneElement);
          drawZoneBorderSide(
            pdf,
            "top",
            toPxNumber(hs.borderTopWidth),
            hs.borderTopColor,
            hs.borderTopStyle,
            pageWidth,
            0,
            headerHeight,
          );
          drawZoneBorderSide(
            pdf,
            "right",
            toPxNumber(hs.borderRightWidth),
            hs.borderRightColor,
            hs.borderRightStyle,
            pageWidth,
            0,
            headerHeight,
          );
          drawZoneBorderSide(
            pdf,
            "bottom",
            toPxNumber(hs.borderBottomWidth),
            hs.borderBottomColor,
            hs.borderBottomStyle,
            pageWidth,
            0,
            headerHeight,
          );
          drawZoneBorderSide(
            pdf,
            "left",
            toPxNumber(hs.borderLeftWidth),
            hs.borderLeftColor,
            hs.borderLeftStyle,
            pageWidth,
            0,
            headerHeight,
          );
        }

        if (footerZoneElement && index === canvases.length - 1) {
          const fs = normalizeElementStyles(footerZoneElement);
          const footerTop = pageHeight - footerHeight;
          drawZoneBorderSide(
            pdf,
            "top",
            toPxNumber(fs.borderTopWidth),
            fs.borderTopColor,
            fs.borderTopStyle,
            pageWidth,
            footerTop,
            pageHeight,
          );
          drawZoneBorderSide(
            pdf,
            "right",
            toPxNumber(fs.borderRightWidth),
            fs.borderRightColor,
            fs.borderRightStyle,
            pageWidth,
            footerTop,
            pageHeight,
          );
          drawZoneBorderSide(
            pdf,
            "bottom",
            toPxNumber(fs.borderBottomWidth),
            fs.borderBottomColor,
            fs.borderBottomStyle,
            pageWidth,
            footerTop,
            pageHeight,
          );
          drawZoneBorderSide(
            pdf,
            "left",
            toPxNumber(fs.borderLeftWidth),
            fs.borderLeftColor,
            fs.borderLeftStyle,
            pageWidth,
            footerTop,
            pageHeight,
          );
        }

        // Header overlays (logo/header text) are drawn directly without masking
        // the exported canvas content.
        // Add BackGroungImg if present new start
        const backgroundImg = usedElements.find(
          (x) => x.fieldPlaceHolder === "{BACKGROUND_IMAGE}",
        );
        if (backgroundImg) {
          pdf.saveGraphicsState();
          pdf.setGState(new pdf.GState({ opacity: 1 }));
          const backGroundImage = `${backgroundImg.fieldName}`;
          const imgProps = pdf?.getImageProperties(backGroundImage);
          // const width = 150; // Adjust width as needed
          // const height = (imgProps.height * width) / imgProps.width;
          const width = pageWidth; // Adjust width in full page width.
          const height = pageHeight; // Adjust width in full page height.
          pdf.addImage(
            backGroundImage,
            "PNG",
            backgroundImg.x,
            backgroundImg.y,
            width,
            height,
            null,
            "FAST",
          );
          pdf.restoreGraphicsState();
        }
        //  Add BackGroungImg if present new end
        const companyLogo = usedElements.find(
          (x) => x.fieldPlaceHolder === "{COMPANY_LOGO}",
        );
        if (companyLogo && companyLogo.fieldName) {
          const companyLogoImage = `${companyLogo.fieldName}`;
          const imgProps = pdf.getImageProperties(companyLogoImage);
          const companyLogoStyles = normalizeElementStyles(companyLogo);
          const companyLogoSize = getStyledImageDimensions(
            companyLogoStyles,
            imgProps,
            toPxNumber(companyLogoStyles.width, 120),
          );

          if (index === 0) {
            pdf.addImage(
              companyLogoImage,
              "PNG",
              Number(companyLogo.x) || 0,
              Number(companyLogo.y) || 0,
              companyLogoSize.width,
              companyLogoSize.height,
              null,
              "FAST",
            );
          } else {
            const companySecondaryLogo = usedElements.find(
              (x) => x.fieldPlaceHolder === "{Secondary_Logo}",
            );
            if (companySecondaryLogo && companySecondaryLogo.fieldName) {
              const companySecondaryImage = `${companySecondaryLogo.fieldName}`;
              const imgProps = pdf.getImageProperties(companySecondaryImage);
              const secondaryLogoStyles =
                normalizeElementStyles(companySecondaryLogo);
              const secondaryLogoSize = getStyledImageDimensions(
                secondaryLogoStyles,
                imgProps,
                toPxNumber(secondaryLogoStyles.width, 100),
              );
              pdf.addImage(
                companySecondaryImage,
                "PNG",
                Number(companySecondaryLogo.x) || 0,
                Number(companySecondaryLogo.y) || 0,
                secondaryLogoSize.width,
                secondaryLogoSize.height,
                null,
                "FAST",
              );
            }
          }
        }

        pdf.setFontSize(12);
        pdf.setTextColor(50, 50, 50);
        const HeaderText = usedElements.find(
          (x) => x.fieldPlaceHolder === "{Header_Text}",
        );
        if (HeaderText) {
          if (index === 0) {
            const styles = normalizeElementStyles(HeaderText);
            pdf.setFont(
              styles.fontFamily || styles["font-family"] || "Arial",
              styles.fontStyle || styles["font-style"] || "normal",
              styles.fontWeight || styles["font-weight"] || "normal",
            );
            pdf.setFontSize(
              String(styles.fontSize || styles["font-size"] || "12px").replace("px", "") ||
              12,
            );
            const [r, g, b] = hexToRgb(
              styles.color || styles["color"] || "#323232",
            );
            pdf.setTextColor(r, g, b);

            const headerTextContent = stripHtmlWithLineBreaks(
              HeaderText.fieldName,
            );
            console.log("headerTextContent===>", headerTextContent);
            const headerLines = headerTextContent
              .split("\n")
              .filter((line) => line.trim() !== "");
            const xPosition = HeaderText.x;
            let yPosition = headerHeight / 2;
            headerLines.forEach((line) => {
              pdf.text(line.trim(), xPosition, yPosition);
              yPosition += 10; // Adjust spacing for each new line
            });
          }
        }
        // Footer overlays
        pdf.setFontSize(10);
        pdf.setTextColor(50, 50, 50);
        const FooterText = usedElements.find(
          (x) => x.fieldPlaceHolder === "{Footer_Text}",
        );

        if (FooterText && index === canvases.length - 1) {
          const styles = normalizeElementStyles(FooterText);
          pdf.setFont(
            styles.fontFamily || styles["font-family"] || "Arial",
            styles.fontStyle || styles["font-style"] || "normal",
            styles.fontWeight || styles["font-weight"] || "normal",
          );
          pdf.setFontSize(
            String(styles.fontSize || styles["font-size"] || "12px").replace("px", "") ||
            12,
          );
          const [r, g, b] = hexToRgb(
            styles.color || styles["color"] || "#323232",
          );
          pdf.setTextColor(r, g, b);
          const footerTextContent = stripHtmlWithLineBreaks(
            FooterText.fieldName,
          );
          const footerLines = footerTextContent
            .split("\n")
            .filter((line) => line.trim() !== "");
          const xPosition = FooterText.x;
          let yPosition = pageHeight - footerHeight / 2;
          footerLines.forEach((line) => {
            pdf.text(line.trim(), xPosition, yPosition);
            yPosition += 10; // Adjust spacing for each new line
          });
        }

        // Add watermark if present
        const watermark = usedElements.find(
          (x) => x.fieldPlaceHolder === "{WATERMARK}",
        );
        if (watermark) {
          pdf.saveGraphicsState();
          pdf.setGState(new pdf.GState({ opacity: 0.3 }));
          const watermarkImage = `${watermark.fieldName}`;
          const imgProps = pdf.getImageProperties(watermarkImage);
          const width = 150; // Adjust width as needed
          const height = (imgProps.height * width) / imgProps.width;
          pdf.addImage(
            watermarkImage,
            "PNG",
            watermark.x,
            watermark.y,
            width,
            height,
            null,
            "FAST",
          );
          pdf.restoreGraphicsState();
        }

        // Page number (footer style: top-left in footer zone)
        const pageNumberText = `Pagina ${index + 1} / ${canvases.length}`;
        pdf.setFontSize(9);
        pdf.setTextColor(60, 60, 60);
        pdf.text(pageNumberText, 22, pageHeight - footerHeight + 12);
      });
      // Generate dynamic file name

      // for pdf print download start.
      let workspaceName = localStorage.getItem("workspaceName")?.split("-")[0];
      // let selectedProject = localStorage.getItem("projectForPrint");
      let selectedQuoteNo = localStorage.getItem("QuoteNoForPrint");
      let selectedClientName = localStorage.getItem("clientNameForPrint");
      let employeeName = localStorage.getItem("firstname");

      let pdfTitle = `${selectedClientName}-${selectedQuoteNo}-${workspaceName}-${moment(
        new Date(),
      ).format("YYYY.MM.DD, h:mm a")}.pdf`;
      // for pdf print download end.

      const now = new Date();
      const timestamp = `${now.getFullYear()}${String(
        now.getMonth() + 1,
      ).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}_${String(
        now.getHours(),
      ).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}${String(
        now.getSeconds(),
      ).padStart(2, "0")}`;
      const fileName = `download_${timestamp}.pdf`;
      // pdf.save(pdfTitle);

      if (!blockPDFDownload) {
        pdf.save(pdfTitle);
      }

      // for link genrate start
      if (items.genrateLink === "genrateLink") {
        const pdfBlob = pdf.output("blob");
        // const pdfFile = new File([pdfBlob], "myfile.pdf", {
        const pdfFile = new File([pdfBlob], pdfTitle, {
          type: "application/pdf",
        });
        // dispatch(singleUploadToS3(pdfFile));
        if (!uploadLockRef.current) {
          uploadLockRef.current = true;
          dispatch(singleUploadToS3(pdfFile));
        }
        console.log("asc321", pdfFile);
      }
      // for link genrate end

      setIsRequested(false);
      onDownloadComplete && onDownloadComplete();
    });
  };

  const exportToPDF2 = async () => {
    const content = document.getElementById("builder-output-1");

    if (!content) {
      setIsRequested(false);
      onDownloadComplete && onDownloadComplete();
      return;
    }

    await waitForPdfAssetsToSettle(content);

    const pageWidth = BUILDER_CANVAS_WIDTH;
    const pageHeight = BUILDER_CANVAS_HEIGHT;
    const footerHeight = FOOTER_ZONE_HEIGHT;
    const pdf = new jsPDF("p", "px", [pageWidth, pageHeight]);
    const tempContainers = [];
    const validRefs = elementsRef.current.filter(
      (elementRef) => elementRef?.el && elementRef?.item,
    );
    const pageModels = buildPageModels(validRefs);
    const pageCount = Math.max(1, pageModels.length);
    const createPageClone = () => {
      const pageClone = document.createElement("div");
      pageClone.style.position = "absolute";
      pageClone.style.left = "-20000px";
      pageClone.style.top = "0px";
      pageClone.style.width = `${pageWidth}px`;
      pageClone.style.height = `${pageHeight}px`;
      pageClone.style.overflow = "hidden";
      pageClone.style.backgroundColor = "#ffffff";
      pageClone.style.boxSizing = "border-box";
      return pageClone;
    };
    const shouldRenderFixedNodeOnPage = (elementRef, pageIndex) => {
      const placeholder = normalizePlaceholder(
        elementRef?.item?.fieldPlaceHolder,
      );
      if (isAllPageFixedPlaceholder(placeholder)) {
        return true;
      }
      const elementTop = getElementTop(elementRef);
      if (elementTop < HEADER_ZONE_HEIGHT) {
        return pageIndex === 0;
      }
      if (elementTop >= pageHeight - FOOTER_ZONE_HEIGHT) {
        return pageIndex === pageCount - 1;
      }
      return false;
    };
    const appendFixedNodes = (pageClone, pageIndex) => {
      const fixedRefs = validRefs
        .filter((elementRef) => {
          const placeholder = normalizePlaceholder(
            elementRef?.item?.fieldPlaceHolder,
          );
          return (
            isAllPageFixedPlaceholder(placeholder) ||
            isHeaderZoneRef(elementRef) ||
            isFooterZoneRef(elementRef)
          );
        })
        .sort(
          (a, b) =>
            getElementTop(a) - getElementTop(b) ||
            getElementLeft(a) - getElementLeft(b),
        );

      fixedRefs.forEach((elementRef) => {
        if (!shouldRenderFixedNodeOnPage(elementRef, pageIndex)) {
          return;
        }
        const clone = cloneNodeForPage(
          elementRef,
          getElementTop(elementRef),
          getElementLeft(elementRef),
        );
        if (clone) {
          pageClone.appendChild(clone);
        }
      });
    };
    const appendBodyNodes = (pageClone, bodyNodes) => {
      bodyNodes.forEach((bodyNode) => {
        const clone = cloneNodeForPage(bodyNode.ref, bodyNode.top, bodyNode.left);
        if (clone) {
          pageClone.appendChild(clone);
        }
      });
    };
    const appendTableSegment = (pageClone, tableSegment) => {
      if (!tableSegment?.ref) {
        return;
      }
      const clone = cloneTableSegmentForPage(
        tableSegment.ref,
        tableSegment.top,
        tableSegment.left,
        tableSegment.rowIndexes,
        tableSegment.width,
      );
      if (clone) {
        pageClone.appendChild(clone);
      }
    };

    try {
      for (let index = 0; index < pageCount; index += 1) {
        const pageClone = createPageClone();
        appendFixedNodes(pageClone, index);
        appendBodyNodes(pageClone, pageModels[index]?.bodyNodes || []);
        appendTableSegment(pageClone, pageModels[index]?.tableSegment);
        document.body.appendChild(pageClone);
        tempContainers.push(pageClone);

        // eslint-disable-next-line no-await-in-loop
        await waitForImages(pageClone);

        // eslint-disable-next-line no-await-in-loop
        const canvas = await html2canvas(pageClone, {
          width: pageWidth,
          height: pageHeight,
          windowWidth: pageWidth,
          windowHeight: pageHeight,
          y: 0,
          x: 0,
          scale: 3.5,
          removeContainer: true,
          backgroundColor: "#ffffff",
          useCORS: true,
        });

        const imgData = canvas.toDataURL("image/png");
        if (index > 0) {
          pdf.addPage();
        }
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, pageHeight, "F");
        pdf.addImage(imgData, "PNG", 0, 0, pageWidth, pageHeight);
        const pageNumberText = `Pagina ${index + 1} / ${pageCount}`;
        pdf.setFontSize(9);
        pdf.setTextColor(60, 60, 60);
        pdf.text(pageNumberText, 22, pageHeight - footerHeight + 12);
      }

      const workspaceName = localStorage.getItem("workspaceName")?.split("-")[0];
      const selectedQuoteNo = localStorage.getItem("QuoteNoForPrint");
      const selectedClientName = localStorage.getItem("clientNameForPrint");
      const pdfTitle = `${selectedClientName}-${selectedQuoteNo}-${workspaceName}-${moment(
        new Date(),
      ).format("YYYY.MM.DD, h:mm a")}.pdf`;

      if (!blockPDFDownload) {
        pdf.save(pdfTitle);
      }

      if (items.genrateLink === "genrateLink") {
        const pdfBlob = pdf.output("blob");
        const minimumPdfSize = 2048;
        if (pdfBlob.size < minimumPdfSize) {
          if (pdfUploadRetryRef.current < 1) {
            pdfUploadRetryRef.current += 1;
            setTimeout(() => {
              exportToPDF2();
            }, 750);
            return;
          }

          console.error("Generated PO PDF is empty or too small to upload.");
          setIsRequested(false);
          onDownloadComplete && onDownloadComplete();
          return;
        }

        const pdfFile = new File([pdfBlob], pdfTitle, {
          type: "application/pdf",
        });
        if (!uploadLockRef.current) {
          uploadLockRef.current = true;
          dispatch(singleUploadToS3(pdfFile));
        }
      }
    } finally {
      tempContainers.forEach((node) => {
        if (node?.parentNode) {
          node.parentNode.removeChild(node);
        }
      });
      setIsRequested(false);
      onDownloadComplete && onDownloadComplete();
    }
  };

  function processDivs() {
    // Step 1: Gather all div elements
    const divs = document.querySelectorAll('[id^="print-element-"]');

    // Step 2: Group divs by 'top' position
    const groupedDivs = {};

    divs.forEach((div) => {
      const top = div.style.top;
      if (!groupedDivs[top]) {
        groupedDivs[top] = [];
      }
      groupedDivs[top].push(div);
    });

    // Step 3: Create tables for each group
    let contentHTML = ""; // String to accumulate the generated HTML content

    Object.keys(groupedDivs).forEach((top) => {
      const divGroup = groupedDivs[top];
      const tableDivs = divGroup.filter((div) => div.querySelector("table"));
      const nonTableDivs = divGroup.filter(
        (div) => !div.querySelector("table"),
      );

      // Preserve existing table blocks to keep configured table structure/styles.
      if (tableDivs.length > 0) {
        tableDivs.forEach((div) => {
          contentHTML += `<div>${div.innerHTML}</div>`;
        });
      }

      if (nonTableDivs.length === 0) {
        return;
      }

      // Check if any div in this group has the border '1px solid black' or is a paragraph-with-border
      let hasBlackBorder = false;

      nonTableDivs.forEach((div) => {
        if (hasParagraphWithBorder(div)) {
          hasBlackBorder = true;
          console.log("Div with Paragrah===>", div.innerHTML);
          // If div has class 'paragraph-with-border', remove all inline styles
          if (hasParagraphWithBorder(div)) {
            // Remove inline styles from the paragraph-with-border divs
            div.removeAttribute("style");
          }
        }
      });

      // Step 4: Create a table for each group
      contentHTML += '<table style="width: 100%; border-collapse: collapse;">';

      // If any div in this group has the '1px solid black' border or is a paragraph-with-border, add border to the table
      if (hasBlackBorder) {
        contentHTML += '<tr style="border: 1px solid black;">'; // Add border to table row
      } else {
        contentHTML += "<tr>"; // No border for this group
      }

      // Loop through the divGroup and add each div's content into table cells (td)
      nonTableDivs.forEach((div) => {
        const td = document.createElement("td"); // Create a table cell for each div

        // If this div or any nested div has the class 'paragraph-with-border', apply the border to the table
        if (hasParagraphWithBorder(div)) {
          // Apply the border to the table cell and put the content inside
          td.style.border = "1px solid black";
          td.innerHTML = div.innerHTML; // Copy the content of the div into the cell
          contentHTML += td.outerHTML; // Append the td's HTML to the table row
        } else {
          // Otherwise, just add the content to the cell without extra border
          td.innerHTML = div.innerHTML; // Place div content inside the td
          contentHTML += td.outerHTML; // Append the td's HTML to the table row
        }
      });

      contentHTML += "</tr>"; // Close the row tag
      contentHTML += "</table>"; // Close the table tag
    });

    return contentHTML; // Return the generated HTML
  }

  // Helper function to check if any nested div contains the class 'paragraph-with-border'
  function hasParagraphWithBorder(div) {
    // Check if the div itself has the class
    if (div.classList.contains("paragraph-with-border")) {
      div.removeAttribute("style");
      return true;
    }

    // Recursively check for the class in any child elements
    const children = div.getElementsByTagName("*");
    for (let child of children) {
      if (child.classList.contains("paragraph-with-border")) {
        child.removeAttribute("style");
        return true;
      }
    }

    return false;
  }
  const exportToDocx = () => {
    const content = document.getElementById("builder-output-1");
    if (!content) {
      console.error("Content not found.");
      return;
    }

    console.log("Original HTML Content:", content.innerHTML);

    const usedElements = items?.data ? items.data : items;

    // Extract header, footer, and logo details
    const HeaderText = usedElements.find(
      (x) => x.fieldPlaceHolder === "{Header_Text}",
    );
    const FooterText = usedElements.find(
      (x) => x.fieldPlaceHolder === "{Footer_Text}",
    );
    const companyLogo = usedElements.find(
      (x) => x.fieldPlaceHolder === "{COMPANY_LOGO}",
    );

    // Prepare the header HTML using table for better layout control in DOCX
    let headerHTML = `
            <table style="width: 100%; border: none;">
                <tr>
                    <td style="width: 100px; vertical-align: top;">
        `;

    // Add company logo on the left
    if (companyLogo) {
      const companyLogoImage = `${companyLogo.fieldName}`;
      headerHTML += `
                <img src="${companyLogoImage}" style="max-width: 100px; height: auto;" alt="Company Logo"/>
            `;
    }

    headerHTML += `</td><td style="text-align: left; vertical-align: top; padding-left: 10px;">`;

    // Add header text on the right (next to the logo)
    if (HeaderText) {
      const headerTextContent = stripHtmlWithLineBreaks(HeaderText.fieldName);
      const headerLines = headerTextContent
        .split("\n")
        .filter((line) => line.trim() !== "");
      headerLines.forEach((line) => {
        headerHTML += `<p style="margin: 5px 0;">${line.trim()}</p>`;
      });
    }

    headerHTML += `</td></tr></table>`;

    // Prepare the footer HTML
    let footerHTML = "";
    if (FooterText) {
      const footerTextContent = stripHtmlWithLineBreaks(FooterText.fieldName);
      const footerLines = footerTextContent
        .split("\n")
        .filter((line) => line.trim() !== "");
      footerHTML += `<div style="text-align: right; font-size: 10px; margin-top: 10px; position: absolute; bottom: 0; right: 0; width: 100%;">`;
      footerLines.forEach((line) => {
        footerHTML += `<p style="margin: 0;">${line.trim()}</p>`;
      });
      footerHTML += `</div>`;
    }

    // Generate dynamic table from divs using processDivs function
    const dynamicTableHTML = processDivs();

    console.log("dynamicTableHTML==========>", dynamicTableHTML);

    // Combine header, dynamic table content, and footer
    const simplifiedHTML = `
            ${headerHTML}
            <div>${dynamicTableHTML}</div>
            ${footerHTML}
        `;

    // Validate the content
    if (!simplifiedHTML.trim()) {
      console.error("Content is empty or invalid.");
      return;
    }

    try {
      const docxBlob = htmlDocx.asBlob(simplifiedHTML);

      if (!docxBlob) {
        console.error("Failed to generate DOCX blob.");
        return;
      }

      // Generate dynamic file name
      const now = new Date();
      const timestamp = `${now.getFullYear()}${String(
        now.getMonth() + 1,
      ).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}_${String(
        now.getHours(),
      ).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}${String(
        now.getSeconds(),
      ).padStart(2, "0")}`;
      const fileName = `download_${timestamp}.docx`;

      // Trigger download
      const link = document.createElement("a");
      link.href = URL.createObjectURL(docxBlob);
      link.download = fileName;
      link.click();
    } catch (error) {
      console.error("Error generating DOCX:", error);
    }
  };

  const handleExportRequest = () => {
    uploadLockRef.current = false;
    pdfUploadRetryRef.current = 0;
    if (!disabled) {
      onRequestData();
      setIsRequested(true);
    }
  };

  // genrate link for template pdf while send Email start
  const singleUploadToS3Res = useSelector(
    (state) => state.user.singleUploadToS3Res,
  );

  console.log("asc3212", genrateLink);
  useEffect(() => {
    if (genrateLink === true) {
      handleExportRequest();
      setGenrateLink(false);
    }
  }, [genrateLink]);

  useEffect(() => {
    const uploadedLinks = Array.isArray(singleUploadToS3Res)
      ? singleUploadToS3Res.filter((link) => typeof link === "string" && link)
      : typeof singleUploadToS3Res === "string" && singleUploadToS3Res
        ? [singleUploadToS3Res]
        : [];

    if (uploadedLinks.length > 0 && typeof childDataLink === "function") {
      console.log("asc screens", uploadedLinks);
      childDataLink(uploadedLinks);
    }
  }, [singleUploadToS3Res]);
  // genrate link for template pdf while send Email end

  const findNearestSmallest = (arr, target) => {
    let nearestSmallest = null;
    let smallestDiff = Infinity;

    for (let num of arr) {
      if (num.y < target) {
        let diff = target - num.y;
        if (diff < smallestDiff) {
          smallestDiff = diff;
          nearestSmallest = num;
        }
      }
    }

    return nearestSmallest;
  };

  const getTopPosition = (usedElements, item) => {
    let newYPosition = item.y;
    const getNearestElement = findNearestSmallest(
      usedElements.filter(
        (x) => x.fieldPlaceHolder !== "{WATERMARK}" || x.id != item.id,
      ),
      item.y,
    );
    if (getNearestElement) {
      let diffrence = item.y - getNearestElement.y;
      let previousElement = document.getElementById(
        `print-element-${getNearestElement.id}`,
      );
      if (previousElement) {
        // newYPosition = previousElement.getBoundingClientRect().bottom + diffrence;
        console.log(
          "offsetTop",
          previousElement.offsetTop,
          getNearestElement.y,
          previousElement.offsetTop -
          previousElement.getBoundingClientRect().height,
          getNearestElement.fieldPlaceHolder,
        );
        // newYPosition = (previousElement.getBoundingClientRect().height - previousElement.offsetTop) + diffrence;
        newYPosition +=
          previousElement.getBoundingClientRect().height -
          previousElement.offsetTop +
          diffrence;
      }
    }
    return newYPosition;
  };

  return (
    <>
      <div onClick={handleExportRequest}>{children}</div>
      {bulderElements && isRequested && (
        <div
          style={{
            position: "absolute",
            opacity: 0,
            zIndex: isRequested ? "unset" : -1000,
          }}
        >
          <div
            style={{
              position: "relative",
              minHeight: `${BUILDER_CANVAS_HEIGHT}px`,
              width: `${BUILDER_CANVAS_WIDTH}px`,
              backgroundColor: "transparent",
              boxSizing: "border-box",
              overflow: "hidden",
            }}
            id="builder-output-1"
          >
            {bulderElements
              ?.filter(
                (x) =>
                  x.fieldPlaceHolder !== "{WATERMARK}" &&
                  x.fieldPlaceHolder !== "{BACKGROUND_IMAGE}",
              )
              .map((item, elIndex) => {
                return (
                  <div
                    key={item.id}
                    ref={(el) => (elementsRef.current[elIndex] = { el, item })}
                    id={`print-element-${item.id}`}
                    style={{
                      zIndex:
                        item.fieldPlaceHolder === "{WATERMARK}" ||
                          item.fieldPlaceHolder === "{BACKGROUND_IMAGE}"
                          ? 9
                          : 10,
                      opacity:
                        item.fieldPlaceHolder === "{WATERMARK}" ||
                          item.fieldPlaceHolder === "{BACKGROUND_IMAGE}"
                          ? 0.2
                          : 1,
                      position: "absolute",
                      left: `${item.x}px`,
                      top: `${item.y}px`,
                      width: "auto",
                      boxSizing: "border-box",
                    }}
                  >
                    <RenderTemplateElement
                      element={item}
                      mode="print"
                      key={`element-${item.id}`}
                    />
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </>
  );
};

export default BuilderOutput;
